import React, { Component } from 'react'
import { Alert, Button, Card, Col, Row, Space, Spin, Tag, Typography, Popover, message } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { getApiModuleList, getVibeAppModules, saveVibeAppModules } from '../../../request'
import {
    DEFAULT_MODULE_HINT,
    SENSITIVE_MAP,
    buildModuleSaveMessage,
    summarizeSelection,
    toModuleDomains,
} from '../vibeModuleCatalog'

const { Text } = Typography

/**
 * 功能模块勾选面板：用于 AMS 应用详情。
 * 取数和保存走 vibeAdmin。
 */
export default class VibeModuleSelector extends Component {
    state = {
        loading: false,
        saving: false,
        loadFailed: false,
        catalog: [],
        selectedCodes: [],
        sensitiveConfirmed: false,
    }

    componentDidMount() {
        this.load()
    }

    componentDidUpdate(prevProps) {
        if (String(prevProps.projectId) !== String(this.props.projectId)) {
            this.load()
        }
    }

    load = () => {
        const { projectId, loadModules } = this.props
        if (!projectId) return
        this.setState({ loading: true, sensitiveConfirmed: false, loadFailed: false })
        Promise.resolve(loadModules(projectId))
            .then((data) => {
                // 读不到模块目录不能当成「这个项目没有模块」，否则一保存就把原有模块清空
                if (!data || (!Array.isArray(data.catalog) && !Array.isArray(data.moduleCodes))) {
                    this.setState({ loadFailed: true })
                    return
                }
                this.setState({
                    catalog: Array.isArray(data?.catalog) ? data.catalog : (data?.catalog?.list || data?.list || []),
                    selectedCodes: Array.isArray(data?.moduleCodes) ? data.moduleCodes : [],
                    sensitiveConfirmed: false,
                    loadFailed: false,
                })
            })
            .catch(() => this.setState({ loadFailed: true }))
            .finally(() => this.setState({ loading: false }))
    }

    toggle = (moduleCode, checked) => {
        const selectedCodes = checked
            ? [...this.state.selectedCodes, moduleCode]
            : this.state.selectedCodes.filter((code) => code !== moduleCode)
        this.setState({ selectedCodes, sensitiveConfirmed: false })
    }

    save = () => {
        const { projectId, saveModules, onSaved, disabled } = this.props
        if (disabled || this.state.loadFailed) return
        this.setState({ saving: true })
        Promise.resolve(saveModules({ projectId, moduleCodes: this.state.selectedCodes }))
            .then((result) => {
                message.success(buildModuleSaveMessage(result))
                this.setState({ sensitiveConfirmed: false })
                if (onSaved) onSaved(result)
                this.load()
            })
            .catch(() => {})
            .finally(() => this.setState({ saving: false }))
    }

    renderModuleHint = (module) => (
        <div style={{ maxWidth: 320 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{module.moduleName}</div>
            {module.sampleQuestions ? (
                <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginBottom: 6 }}>{module.sampleQuestions}</div>
            ) : (
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                    还没填示例问句，可在「功能模块」页补齐，填了会写进使用者的提示词。
                </div>
            )}
            <div style={{ fontSize: 12, color: '#595959' }}>
                接口 {module.apiCount || 0} 个，其中公开 {module.publicApiCount || 0} 个；只有公开的接口会授权给使用者。
            </div>
            <div style={{ fontSize: 12, color: '#595959' }}>权限来源：{module.permissionSource || '未填写'}</div>
            <div style={{ fontSize: 12, color: '#595959' }}>
                敏感级别：{SENSITIVE_MAP[module.sensitiveLevel] || '低'}
                {module.owner ? ` · 负责人：${module.owner}` : ''}
            </div>
            {module.remark ? <div style={{ fontSize: 12, marginTop: 4 }}>{module.remark}</div> : null}
        </div>
    )

    render() {
        const { hint, disabled } = this.props
        const { loading, saving, catalog, selectedCodes, sensitiveConfirmed, loadFailed } = this.state
        const domains = toModuleDomains(catalog)
        const summary = summarizeSelection(catalog, selectedCodes)
        const needSensitiveConfirm = summary.sensitive.length > 0

        return (
            <Spin spinning={loading}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12, borderRadius: 10 }}
                    message={hint?.message || DEFAULT_MODULE_HINT.message}
                    description={hint?.description || DEFAULT_MODULE_HINT.description}
                />
                {loadFailed ? (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginBottom: 12, borderRadius: 10 }}
                        message="模块目录没读到，先不要保存"
                        description="读取项目已配置的模块失败，这时保存会把项目原有的模块清空。点重试重新加载。"
                        action={<Button size="small" onClick={this.load}>重试</Button>}
                    />
                ) : null}
                <Row gutter={16}>
                    <Col span={16}>
                        {domains.map((group) => (
                            <div key={group.domainName} style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>{group.domainName}</div>
                                {group.modules.map((module) => {
                                    const usable = module.status === 'active'
                                    const checked = selectedCodes.includes(module.moduleCode)
                                    return (
                                        <div
                                            key={module.moduleCode}
                                            style={{
                                                border: '1px solid #f0f0f0',
                                                borderRadius: 8,
                                                padding: '10px 12px',
                                                marginBottom: 8,
                                                background: checked ? '#f0f8ff' : '#fff',
                                            }}
                                        >
                                            <Space align="start">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={!usable || disabled}
                                                    onChange={(event) => this.toggle(module.moduleCode, event.target.checked)}
                                                    style={{ marginTop: 4 }}
                                                />
                                                <div>
                                                    <div>
                                                        <Popover trigger="hover" placement="right" title={`${module.moduleName} 能做什么`} content={this.renderModuleHint(module)}>
                                                            <span style={{ cursor: 'help', borderBottom: '1px dashed #bfbfbf' }}>
                                                                <Text strong>{module.moduleName}</Text>
                                                                <QuestionCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                                            </span>
                                                        </Popover>
                                                        <Tag color="blue" style={{ marginLeft: 8 }}>{module.moduleCode}</Tag>
                                                        {usable ? null : (
                                                            <Tag color="default" style={{ marginLeft: 8 }}>
                                                                {module.status === 'draft' ? '草稿' : '停用'}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    <div style={{ marginTop: 4 }}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            接口 {module.apiCount || 0} · 公开 {module.publicApiCount || 0} · 工具 {module.toolCount || 0}
                                                        </Text>
                                                    </div>
                                                    <div style={{ marginTop: 2 }}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            权限来源：{module.permissionSource || '未填写'}
                                                        </Text>
                                                    </div>
                                                </div>
                                            </Space>
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                        {!domains.length ? <Text type="secondary">还没有功能模块</Text> : null}
                    </Col>
                    <Col span={8}>
                        <Card size="small" bordered={false} style={{ background: '#fafafa', borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>已选模块</div>
                            <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.selected.length}</div>
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                                模块共 {summary.totalCount} 个接口，其中公开 {summary.publicCount} 个会授权
                            </div>
                            {summary.totalCount > summary.publicCount ? (
                                <div style={{ fontSize: 12, color: '#d46b08', marginBottom: 6 }}>
                                    有 {summary.totalCount - summary.publicCount} 个接口未公开，不会授权给使用者
                                </div>
                            ) : null}
                            {needSensitiveConfirm ? (
                                <label style={{ display: 'block', fontSize: 12, color: '#d46b08', marginBottom: 6 }}>
                                    <input
                                        type="checkbox"
                                        checked={sensitiveConfirmed}
                                        onChange={(event) => this.setState({ sensitiveConfirmed: event.target.checked })}
                                        style={{ marginRight: 6 }}
                                    />
                                    含敏感模块（{summary.sensitive.map((module) => module.moduleName).join('、')}），我已确认
                                </label>
                            ) : null}
                            <Button
                                type="primary"
                                block
                                loading={saving}
                                disabled={disabled || loadFailed || (needSensitiveConfirm && !sensitiveConfirmed)}
                                style={{ marginTop: 8 }}
                                onClick={this.save}
                            >
                                保存功能模块
                            </Button>
                        </Card>
                    </Col>
                </Row>
            </Spin>
        )
    }
}

VibeModuleSelector.defaultProps = {
    loadModules: (projectId) => Promise.all([
        getApiModuleList({}).catch(() => []),
        getVibeAppModules({ projectId }).catch(() => null),
    ]).then(([catalog, selected]) => ({ catalog, moduleCodes: selected?.moduleCodes || [] })),
    saveModules: saveVibeAppModules,
}
